import { useEffect, useState } from "react";
import {
  PencilIcon,
  UsersIcon,
  BriefcaseIcon,
  CloudIcon,
} from "@heroicons/react/24/outline";
import { Card, Button, Badge, Input, Select } from "../../../../../shared";
import { AddressForm } from "../../../../../components";
import { formatDate } from "../../../../../utils";
import { useUpdateAdminCustomerMutation } from "../../../../../redux/services/admin/adminApi";
import { toast } from "react-toastify";

const formatBytes = (bytes = 0) => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 Bytes";
  }

  const units = ["Bytes", "KB", "MB", "GB", "TB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  const value = bytes / 1024 ** unitIndex;
  const precision = value >= 10 || unitIndex === 0 ? 0 : 1;

  return `${value.toFixed(precision)} ${units[unitIndex]}`;
};

const formatList = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) {
    return "None";
  }

  return items.join(", ");
};

const hasCustomerApiKey = (customer = {}) =>
  Boolean(customer?.metadata?.apiKeyPreview);

const buildAddressForm = (customer = {}) => ({
  address: customer?.address?.address || "",
  suite: customer?.address?.suite || "",
  city: customer?.address?.city || "",
  state: customer?.address?.state || "",
  country: customer?.address?.country || "",
  zipcode: customer?.address?.zipcode || "",
});

const APP_TYPE_OPTIONS = [
  { value: "trucking", label: "Trucking" },
  { value: "serviceRepair", label: "Service Repair" },
];

const buildProfileForm = (customer = {}) => ({
  name: customer?.name || "",
  industry: customer?.industry || "",
  phone: customer?.phone || "",
  email: customer?.email || "",
  appType: customer?.appType || "",
});

const getEventValue = (event) =>
  event?.target ? event.target.value : event?.value;

const SectionActions = ({ isEditing, isSaving, onEditToggle, onSave }) => (
  <div className="flex items-center gap-2">
    <Button type="alternative" size="sm" onClick={onEditToggle}>
      <PencilIcon className="h-4 w-4 mr-2" />
      {isEditing ? "Cancel" : "Edit"}
    </Button>
    {isEditing ? (
      <Button size="sm" loading={isSaving} onClick={onSave}>
        Save
      </Button>
    ) : null}
  </div>
);

const ReadOnlyField = ({ label, value }) => (
  <div>
    <p className="font-medium">{label}</p>
    <p className="text-gray-500">{value || "Not provided"}</p>
  </div>
);

const CustomerHealth = ({ customer }) => (
  <Card>
    <h3 className="text-lg font-medium mb-4">Customer Health</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
      <StatCard
        label="Status"
        value={customer?.active ? "Active" : "Pending"}
        icon={<UsersIcon className="h-5 w-5" />}
      />
      <StatCard
        label="Onboarding"
        value={customer?.onboardingStatus?.label || "Draft"}
        icon={<UsersIcon className="h-5 w-5" />}
        subtext={customer?.onboardingStatus?.description}
      />
      <StatCard
        label="Billing"
        value={customer?.paymentStatus || "pending"}
        icon={<BriefcaseIcon className="h-5 w-5" />}
      />
      <StatCard
        label="Release Channel"
        value={customer?.releaseChannel || "stable"}
        icon={<CloudIcon className="h-5 w-5" />}
      />
      <StatCard
        label="Plan Tier"
        value={customer?.planTier || "tier1"}
        icon={<CloudIcon className="h-5 w-5" />}
      />
    </div>
  </Card>
);

// Sub-components for better organization
const CompanyProfile = ({
  customer,
  form,
  isEditing,
  isSaving,
  onEditToggle,
  onFormChange,
  onSave,
}) => (
  <Card>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-medium">Company Profile</h3>
      <SectionActions
        isEditing={isEditing}
        isSaving={isSaving}
        onEditToggle={onEditToggle}
        onSave={onSave}
      />
    </div>

    {isEditing ? (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Input
            id="name"
            label="Company Name"
            value={form.name}
            onChange={onFormChange}
          />
          <Input
            id="industry"
            label="Industry"
            value={form.industry}
            onChange={onFormChange}
          />
          <Input
            id="phone"
            type="number"
            label="Phone"
            value={form.phone}
            onChange={onFormChange}
          />
        </div>

        <div className="space-y-4">
          <Input
            id="email"
            type="email"
            label="Email"
            value={form.email}
            onChange={onFormChange}
          />
          <Select
            id="appType"
            label="App Type"
            value={APP_TYPE_OPTIONS.find((option) => option.value === form.appType)?.label}
            options={APP_TYPE_OPTIONS}
            onChange={onFormChange}
          />
          <div>
            <p className="text-sm text-gray-500">Created</p>
            <p>{formatDate(customer?.dateCreated)}</p>
          </div>
        </div>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
        <div className="space-y-4">
          <ReadOnlyField label="Company Name" value={form.name} />
          <ReadOnlyField label="Industry" value={form.industry} />
          <ReadOnlyField label="Phone" value={form.phone} />
        </div>

        <div className="space-y-4">
          <ReadOnlyField label="Email" value={form.email} />
          <ReadOnlyField
            label="App Type"
            value={APP_TYPE_OPTIONS.find((option) => option.value === form.appType)?.label}
          />
          <ReadOnlyField label="Created" value={formatDate(customer?.dateCreated)} />
        </div>
      </div>
    )}
  </Card>
);

const AddressSection = ({
  customer,
  form,
  isEditing,
  isSaving,
  onEditToggle,
  onFormChange,
  onSave,
}) => (
  <Card>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-medium">Address</h3>
      <SectionActions
        isEditing={isEditing}
        isSaving={isSaving}
        onEditToggle={onEditToggle}
        onSave={onSave}
      />
    </div>

    {isEditing ? (
      <AddressForm
        form={form}
        updateForm={onFormChange}
        inputSize="md"
      />
    ) : (
      <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
        <ReadOnlyField label="Street Address" value={form.address} />
        <ReadOnlyField label="Suite" value={form.suite} />
        <ReadOnlyField label="City" value={form.city} />
        <ReadOnlyField label="State/Province" value={form.state} />
        <ReadOnlyField label="Country" value={form.country} />
        <ReadOnlyField label="Zipcode" value={form.zipcode} />
      </div>
    )}
  </Card>
);

const ConfigurationSection = ({ customer, onEmailConfig, onApiKey }) => (
  <Card>
    <h3 className="text-lg font-medium mb-4">Configuration</h3>
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium">Email Configuration</p>
          <p className="text-sm text-gray-500">
            {customer?.emailConfig?.enabled
              ? "Custom email server configured"
              : "Using default email server"}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge color={customer?.emailConfig?.enabled ? "green" : "red"}>
            {customer?.emailConfig?.enabled ? "Enabled" : "Disabled"}
          </Badge>
          <Button variant="outline" size="sm" onClick={onEmailConfig}>
            Configure
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium">API Access</p>
          <p className="text-sm text-gray-500">
            {hasCustomerApiKey(customer) ? "API key generated" : "No API access"}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge color={hasCustomerApiKey(customer) ? "green" : "gray"}>
            {hasCustomerApiKey(customer) ? "Enabled" : "Disabled"}
          </Badge>
          <Button variant="outline" size="sm" onClick={onApiKey}>
            {hasCustomerApiKey(customer) ? "Regenerate" : "Generate"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <p className="font-medium">Beta Allowlist</p>
          <p className="text-gray-500">{formatList(customer?.betaAllowlist)}</p>
        </div>
        <div>
          <p className="font-medium">Onboarding Status</p>
          <p className="text-gray-500">
            {customer?.onboardingStatus?.label || "Draft"}
          </p>
        </div>
      </div>
    </div>
  </Card>
);

const UsageStats = ({ customer }) => (
  <Card>
    <h3 className="text-lg font-medium mb-4">Usage Statistics</h3>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <StatCard
        label="Active Users"
        value={customer?.contacts?.length || 0}
        icon={<UsersIcon className="h-5 w-5" />}
      />
      <StatCard
        label="Total Jobs"
        value={customer?.jobCount || 0}
        icon={<BriefcaseIcon className="h-5 w-5" />}
      />
      <StatCard
        label="Storage Used"
        value={formatBytes(customer?.storageUsed || 0)}
        icon={<CloudIcon className="h-5 w-5" />}
      />
    </div>
  </Card>
);

const StatCard = ({ label, value, icon, subtext }) => (
  <div className="p-4 bg-gray-50 rounded-lg">
    <div className="flex items-center space-x-2 text-gray-500">
      {icon}
      <span className="text-sm">{label}</span>
    </div>
    <p className="text-2xl font-semibold mt-2">{value}</p>
    {subtext ? <p className="text-sm text-gray-500 mt-2">{subtext}</p> : null}
  </div>
);

// Main Overview Component
const Overview = ({ customer, onboardingStatus, onEmailConfig, onApiKey }) => {
  const [isProfileEditing, setIsProfileEditing] = useState(false);
  const [isAddressEditing, setIsAddressEditing] = useState(false);
  const [profileForm, setProfileForm] = useState(buildProfileForm(customer));
  const [addressForm, setAddressForm] = useState(buildAddressForm(customer));
  const [updateCustomer, { isLoading: isSavingCustomer }] =
    useUpdateAdminCustomerMutation();
  const customerWithOnboarding = {
    ...customer,
    onboardingStatus,
  };

  useEffect(() => {
    setProfileForm(buildProfileForm(customer));
    setAddressForm(buildAddressForm(customer));
  }, [customer]);

  function handleFormChange(event, setForm) {
    const value = getEventValue(event);
    setForm((current) => ({
      ...current,
      [event.id]: value,
    }));
  }

  function toggleEditState(isEditing, setIsEditing, resetForm) {
    if (isEditing) {
      resetForm();
      setIsEditing(false);
      return;
    }

    setIsEditing(true);
  }

  async function saveSection(data, successToastId, successMessage, errorToastId, errorMessage) {
    try {
      await updateCustomer({
        id: customer._id,
        data,
      }).unwrap();
      toast(successMessage, {
        type: "success",
        toastId: successToastId,
      });
      return true;
    } catch (error) {
      toast(error?.data?.message || errorMessage, {
        type: "error",
        toastId: errorToastId,
      });
      return false;
    }
  }

  function handleProfileFormChange(event) {
    handleFormChange(event, setProfileForm);
  }

  function handleProfileEditToggle() {
    toggleEditState(isProfileEditing, setIsProfileEditing, () =>
      setProfileForm(buildProfileForm(customer))
    );
  }

  async function handleProfileSave() {
    const saved = await saveSection(
      { ...profileForm },
      "customer-details-updated",
      "Customer details updated",
      "customer-details-update-error",
      "Failed to update customer details"
    );

    if (saved) {
      setIsProfileEditing(false);
    }
  }

  function handleAddressFormChange(event) {
    handleFormChange(event, setAddressForm);
  }

  function handleAddressEditToggle() {
    toggleEditState(isAddressEditing, setIsAddressEditing, () =>
      setAddressForm(buildAddressForm(customer))
    );
  }

  async function handleAddressSave() {
    const saved = await saveSection(
      { ...addressForm },
      "customer-address-updated",
      "Customer address updated",
      "customer-address-update-error",
      "Failed to update customer address"
    );

    if (saved) {
      setIsAddressEditing(false);
    }
  }

  return (
    <div className="space-y-6">
      <CustomerHealth customer={customerWithOnboarding} />
      <CompanyProfile
        customer={customer}
        form={profileForm}
        isEditing={isProfileEditing}
        isSaving={isSavingCustomer}
        onEditToggle={handleProfileEditToggle}
        onFormChange={handleProfileFormChange}
        onSave={handleProfileSave}
      />

      <AddressSection
        customer={customer}
        form={addressForm}
        isEditing={isAddressEditing}
        isSaving={isSavingCustomer}
        onEditToggle={handleAddressEditToggle}
        onFormChange={handleAddressFormChange}
        onSave={handleAddressSave}
      />

      <ConfigurationSection
        customer={customerWithOnboarding}
        onEmailConfig={onEmailConfig}
        onApiKey={onApiKey}
      />

      <UsageStats customer={customer} />
    </div>
  );
};

export default Overview;
